/**
 * dataClient.js
 *
 * 데이터 fetch 추상화 레이어 — 백엔드 (정적 / R2+Functions / 실제 서버) 를 숨김.
 *
 * 사용:
 *   import { fetchCafesIndex, fetchCafe, fetchPlace, useCafesIndex, useCafe, usePlace }
 *     from './lib/dataClient';
 *
 *   // 함수형 (await)
 *   const index = await fetchCafesIndex();
 *   const cafe  = await fetchCafe('cafe_001');
 *
 *   // React Hook
 *   const { data: index, loading, error } = useCafesIndex();
 *   const { data: cafe, loading } = useCafe(selectedCafeId);
 *
 * 백엔드 분기 — VITE_DATA_BASE_URL 환경변수로:
 *   - 단계 A (정적 청크):  '/data'   ← 기본값
 *   - 단계 B (R2 + Functions): '/api'
 *   - 단계 C (실제 서버): 'https://api.coffee-finder.com'
 *
 * 응답 캐싱:
 *   in-memory Map 으로 중복 fetch 방지. 한 번 받은 청크는 새로고침까지 캐시.
 *   강제 새로고침은 `invalidateCache()` 호출.
 */

import { useEffect, useState } from "react";

// ── 백엔드 분기 ──────────────────────────────────────────
const BASE_URL = import.meta.env.VITE_DATA_BASE_URL || "/data";

// ── 이미지 도메인 ──────────────────────────────────────
// 청크엔 상대 키만 있음 (예: "cafes/cafe_001/1.jpeg")
// 이미지 절대 URL 은 아래 imageUrl() 헬퍼가 런타임에 조립.
// 이미지는 데이터와 별도 도메인에 둘 수 있어서 BASE_URL 과 분리:
//   단계 A:  VITE_IMAGE_BASE_URL='/images'
//   단계 B:  VITE_IMAGE_BASE_URL='https://images.coffee-finder.com'
//   단계 C:  어떤 도메인이든
const IMAGE_BASE_URL = import.meta.env.VITE_IMAGE_BASE_URL || "/images";

export function imageUrl(key) {
  if (!key) return null;
  // 이미 절대 URL 이면 그대로
  if (key.startsWith("http://") || key.startsWith("https://")) return key;
  // / 로 시작하는 절대 경로도 그대로 (legacy 청크 등)
  if (key.startsWith("/")) return key;
  // 상대 키 → BASE_URL 결합
  const base = IMAGE_BASE_URL.replace(/\/$/, "");
  return `${base}/${key}`;
}

export function imageUrls(keys) {
  if (!Array.isArray(keys)) return [];
  return keys.map(imageUrl).filter(Boolean);
}

// ── 엔드포인트 매핑 ──────────────────────────────────────
// 단계 A (/data) 와 B (/api) 가 URL 패턴이 약간 달라서 분기:
//   A: /data/cafes-index.json   /data/cafe/cafe_001.json   /data/place/rest_001.json
//   B: /api/cafes-index         /api/cafe/cafe_001         /api/place/rest_001  (확장자 없음)
//
// 단계 C (실제 서버) 도 보통 .json 안 붙임. 즉 단계 A 만 예외.
function pathFor(kind, id) {
  const isStaticChunk = BASE_URL === "/data" || BASE_URL.endsWith("/data");
  const ext = isStaticChunk ? ".json" : "";
  if (kind === "index") return `${BASE_URL}/cafes-index${ext}`;
  if (kind === "cafe")  return `${BASE_URL}/cafe/${id}${ext}`;
  if (kind === "place") return `${BASE_URL}/place/${id}${ext}`;
  throw new Error(`Unknown kind: ${kind}`);
}

// ── In-memory 캐시 ──────────────────────────────────────
const cache = new Map();      // url → Promise<data>

function fetchJson(url) {
  // 같은 url 동시 호출은 in-flight Promise 공유
  if (cache.has(url)) return cache.get(url);

  const p = fetch(url, { credentials: "same-origin" })
    .then((res) => {
      if (!res.ok) {
        cache.delete(url);
        throw new Error(`Fetch failed: ${url} (${res.status})`);
      }
      return res.json();
    })
    .catch((err) => {
      cache.delete(url);
      throw err;
    });

  cache.set(url, p);
  return p;
}

export function invalidateCache(prefix) {
  if (!prefix) {
    cache.clear();
    return;
  }
  for (const k of cache.keys()) {
    if (k.includes(prefix)) cache.delete(k);
  }
}

// ── 함수형 API ──────────────────────────────────────────
export async function fetchCafesIndex() {
  return fetchJson(pathFor("index"));
}

export async function fetchCafe(id) {
  if (!id) throw new Error("cafe id required");
  return fetchJson(pathFor("cafe", id));
}

export async function fetchPlace(id) {
  if (!id) throw new Error("place id required");
  return fetchJson(pathFor("place", id));
}

// ── React Hook API ──────────────────────────────────────
function useResource(fetcher, deps) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.resolve()
      .then(fetcher)
      .then((d) => {
        if (cancelled) return;
        setData(d);
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        console.warn("[dataClient]", e);
        setError(e);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, loading, error };
}

export function useCafesIndex() {
  return useResource(() => fetchCafesIndex(), []);
}

export function useCafe(id) {
  return useResource(
    () => (id ? fetchCafe(id) : Promise.resolve(null)),
    [id]
  );
}

export function usePlace(id) {
  return useResource(
    () => (id ? fetchPlace(id) : Promise.resolve(null)),
    [id]
  );
}

// ── 개발용 디버그 ────────────────────────────────────────
if (typeof window !== "undefined" && import.meta.env.DEV) {
  window.__dataClient = {
    BASE_URL,
    cache,
    invalidateCache,
    fetchCafesIndex,
    fetchCafe,
    fetchPlace,
  };
}
